/*============================================================
   REACT DEPENDENCIES
============================================================*/
import { useState, useEffect } from "react";

/*============================================================
   REDUX DEPENDENCIES
============================================================*/
import { useDispatch, useSelector } from "react-redux";

/*============================================================
  MATERIAL UI
============================================================*/
import Grid from "@mui/material/Grid2";

/*============================================================
  PROPRIETARY - COMPONENTS
============================================================*/
import GridDropdown from "../../widgets/GridDropdown";
import GridSectionHeader from "../../widgets/GridSectionHeader";
import GridTextfield from "../../widgets/GridTextfield";

/*============================================================
  PROPRIETARY - REDUX DATA SLICES
============================================================*/
import { updateClientInfo } from "../../../states/slcPersonalInformation.js";

/*============================================================
  PROPRIETARY - DATA MODELS
============================================================*/
import { createPersonalInfo } from "./PersonalInfoModel.js";

/*============================================================
  PROPRIETARY - UTILS
============================================================*/
import { reNumbers, reEmail } from "../../../utils/fieldValidator.js";
import { createPayload, createReduxAction } from "../../../utils/modelHelper.js";
import countryPhoneCode from "../../../data/countryPhoneCodes.json";

export default function SectionContact() {
  const currApplicant = useSelector((state) => state.app.currApplicant);
  const personalInfo = useSelector((state) => state.personalInformation[currApplicant]);

  const [countryCodeMobile, setCountryCodeMobile] = useState(personalInfo.mobilecountrycode);
  const [mobileNumber, setMobileNumber] = useState(personalInfo.mobilephone);
  const [email, setEmail] = useState(personalInfo.email);
  const [countryCodeHome, setCountryCodeHome] = useState(personalInfo.homecountrycode);
  const [homeNumber, setHomeNumber] = useState(personalInfo.homephone);
  const [countryCodeOffice, setCountryCodeOffice] = useState(personalInfo.officecountrycode);
  const [officeNumber, setOfficeNumber] = useState(personalInfo.officephone);

  const [countryCodeMobileError, setCountryCodeMobileError] = useState();
  const [mobileNumberError, setMobileNumberError] = useState();
  const [emailError, setEmailError] = useState();
  const [countryCodeHomeError, setCountryCodeHomeError] = useState();
  const [homeNumberError, setHomeNumberError] = useState();
  const [countryCodeOfficeError, setCountryCodeOfficeError] = useState();
  const [officeNumberError, setOfficeNumberError] = useState();

  const dispatch = useDispatch();

  //REFRESH COMPONENT WITH CURRENT APPLICANTE DATA
  useEffect(() => {
    setCountryCodeMobile(personalInfo.mobilecountrycode);
    setMobileNumber(personalInfo.mobilephone);
    setEmail(personalInfo.email);
    setCountryCodeHome(personalInfo.homecountrycode);
    setHomeNumber(personalInfo.homephone);
    setCountryCodeOffice(personalInfo.officecountrycode);
    setOfficeNumber(personalInfo.officephone);
  }, [currApplicant]);

  function handleChange(e, object) {
    let id = "";

    let actionType = "";
    let updateAction = null;
    let payload = null;

    const personalInfo = createPersonalInfo();

    if (e instanceof PointerEvent) {
      id = object.props.id;

      if (id === "countrycodemobile") {
        personalInfo.mobilecountrycode = e.target.value;
        setCountryCodeMobile(e.target.value);
        actionType = "UPDATE_COUNTRY_CODE_MOBILE";
      }

      if (id === "countrycodehome") {
        personalInfo.homecountrycode = e.target.value;
        setCountryCodeHome(e.target.value);
        actionType = "UPDATE_COUNTRY_CODE_HOME";
      }

      if (id === "countrycodeoffice") {
        personalInfo.officecountrycode = e.target.value;
        setCountryCodeOffice(e.target.value);
        actionType = "UPDATE_COUNTRY_CODE_OFFICE";
      }
    } else {
      id = e.target.id.toLowerCase();

      if (id === "mobilenumber" && reNumbers.test(e.target.value)) {
        if (e.target.value.length === 0) {
          setMobileNumberError("Mobile number is required.");
        } else if (e.target.value.length > 12) {
          setMobileNumberError("Mobile number exceeded 12 characters");
        } else setMobileNumberError("");

        personalInfo.mobilephone = e.target.value;
        setMobileNumber(e.target.value);
        actionType = "UPDATE_MOBILE_NUMBER";
      }

      if (id === "email") {
        if (e.target.value.length === 0) {
          setEmailError("Email is required.");
        } else if (e.target.value.length > 30) {
          setEmailError("Email exceeded 30 characters");
        } else setEmailError("");

        personalInfo.email = e.target.value;
        setEmail(e.target.value);
        actionType = "UPDATE_EMAIL";
      }

      if (id === "homenumber" && reNumbers.test(e.target.value)) {
        if (e.target.value.length === 0) {
          setHomeNumberError("Home number is required.");
        } else if (e.target.value.length > 12) {
          setHomeNumberError("Home number exceeded 12 characters");
        } else setHomeNumberError("");

        personalInfo.homephone = e.target.value;
        setHomeNumber(e.target.value);
        actionType = "UPDATE_HOME_NUMBER";
      }

      if (id === "officenumber" && reNumbers.test(e.target.value)) {
        if (e.target.value.length === 0) {
          setOfficeNumberError("Office number is required.");
        } else if (e.target.value.length > 12) {
          setOfficeNumberError("Office number exceeded 12 characters");
        } else setOfficeNumberError("");

        personalInfo.officephone = e.target.value;
        setOfficeNumber(e.target.value);
        actionType = "UPDATE_OFFICE_NUMBER";
      }
    }

    payload = createPayload(currApplicant, personalInfo);
    updateAction = createReduxAction(actionType, payload);
    dispatch(updateClientInfo(updateAction));
  }

  return (
    <Grid container spacing={2}>
      <GridSectionHeader sectionName="Contact" />
      {/* COUNTRY CODE - MOBILE NUMBER*/}
      <GridDropdown
        id="countrycodemobile"
        key="countrycodemobile"
        columnSize="3"
        label="Country Code"
        currState={countryCodeMobile}
        onChange={handleChange}
        data={countryPhoneCode}
        dataType="countryCode"
      />

      {/* MOBILE NUMBER*/}
      <GridTextfield
        id="mobilenumber"
        key="mobilenumber"
        columnSize="3"
        label="Mobile Number"
        currState={mobileNumber}
        onChange={handleChange}
        error={mobileNumberError}
        helperText={mobileNumberError}
      />

      <GridTextfield
        id="email"
        key="email"
        columnSize="6"
        label="Email"
        currState={email}
        onChange={handleChange}
        error={emailError}
        helperText={emailError}
      />

      {/* COUNTRY CODE - HOME NUMBER*/}
      <GridDropdown
        id="countrycodehome"
        key="countrycodehome"
        columnSize="3"
        label="Country Code"
        currState={countryCodeHome}
        onChange={handleChange}
        data={countryPhoneCode}
        dataType="countryCode"
      />

      {/* HOME NUMBER*/}
      <GridTextfield
        id="homenumber"
        key="homenumber"
        columnSize="3"
        label="Home Number"
        currState={homeNumber}
        onChange={handleChange}
        error={homeNumberError}
        helperText={homeNumberError}
      />

      {/* COUNTRY CODE - OFFICE NUMBER*/}
      <GridDropdown
        id="countrycodeoffice"
        key="countrycodeoffice"
        columnSize="3"
        label="Country Code"
        currState={countryCodeOffice}
        onChange={handleChange}
        data={countryPhoneCode}
        dataType="countryCode"
      />

      {/* OFFICE NUMBER */}
      <GridTextfield
        id="officenumber"
        key="officenumber"
        columnSize="3"
        label="Office Number"
        currState={officeNumber}
        onChange={handleChange}
        error={officeNumberError}
        helperText={officeNumberError}
      />
    </Grid>
  );
}
